import { uuid } from '@oskecare/fe-utilities/scripts/uuid';
import request from '@oskecare/fe-utilities/lib/request';
import { PAYMENT_MANAGEMENT_V2 } from '@oskecare/fe-utilities/lib/constants/dataSources';
import { DEFAULT_CONSTANT_SYMBOL, PAYMENT_STATUS_MAP, PAYMENT_STATUS_TYPES } from './constants.js';

const LOG_SOURCE = 'infra-payment';

/**
 * Module for payment related operations, e.g. initialization and status check.
 *
 * @property {string} _paymentManagementV2ApiUrl
 * @property {Billing} _billing
 * @property {CustomerDataManager} customerDataManager
 * @property {Logger} _logger
 * @property {Map<string, string>} _uncheckedTransactionsCache
 */
export default class Payment {
  /**
   * @param {object} config
   * @param {Billing} billing
   * @param {CustomerDataManager} customerDataManager
   * @param {Logger} logger
   */
  constructor({ config, billing, customerDataManager, logger }) {
    this._paymentManagementV2ApiUrl = config.paymentManagementV2ApiUrl;
    this._billing = billing;
    this._customerDataManager = customerDataManager;
    this._logger = logger;
    this._uncheckedTransactionsCache = new Map();
  }

  /**
   * Get LocalStorage key based on UUID.
   *
   * @param {string} keyUuid
   * @return {string}
   */
  static getTransactionStorageKey(keyUuid) {
    return `ec2-payment-${keyUuid}`;
  }

  /**
   * Proxy for `request` function with logSource pre-set
   *
   * @param {object} params
   * @return {Promise<*>}
   * @see Request.fetch
   * @private
   */
  request(params) {
    return request({
      ...params,
      logSource: LOG_SOURCE,
    }).catch(this._logger.catchError);
  }

  /**
   * Initialize new payment with provided specifications.
   *
   * Note that Billing cache will be invalid after the payment is initialized,
   * thought it's also implicitly invalidated by navigating away from ecare that should occur afterwards.
   *
   * @param {{ id: string, '@type': string }} provider
   * @param {number} amount
   * @param {URL} returnUrl
   * @return {Promise<{storageUuid: string, url: string}>} - url of a payment gateway the consumer should redirect to,
   *   in order to proceed with the payment, and UUID that can be used to associate stored data with the transaction
   */
  async initiatePayment({ provider, amount, returnUrl }) {
    const customerData = await this._customerDataManager.getCustomerData();
    const { billingAccountId } = customerData.cnData;
    const { subscriptionId } = customerData.loggedInSnData;

    const transactionStorageUuid = uuid();
    returnUrl.searchParams.set('code', transactionStorageUuid);

    const transactionData = await this.request({
      method: 'POST',
      route: this._paymentManagementV2ApiUrl,
      body: {
        '@type': 'OSKPayment',
        description: `${billingAccountId}_${subscriptionId}`,
        name: 'invoicePayment',
        paymentDate: new Date().toISOString(),
        totalAmount: {
          value: amount,
          unit: 'EUR',
        },
        paymentMethod: {
          '@type': provider['@type'],
          id: provider.id,
        },
        account: {
          '@referredType': 'BillingAccount',
          id: billingAccountId,
        },
        constantSymbol: DEFAULT_CONSTANT_SYMBOL,
        returnURL: returnUrl,
      },
      dataSource: PAYMENT_MANAGEMENT_V2,
    });

    localStorage.setItem(
      Payment.getTransactionStorageKey(transactionStorageUuid),
      JSON.stringify({
        transactionId: transactionData.id,
        paymentProviderName: provider.name,
      }),
    );

    this._billing.resetCache();
    return {
      storageUuid: transactionStorageUuid,
      url: transactionData.paymentURL,
    };
  }

  /**
   * @param {string} transactionStorageUuid
   * @return {Promise<?{ status: string, statusType: ('success'|'failure'|'unchecked'), paymentProviderName: string }>}
   */
  async getPaymentStatus(transactionStorageUuid) {
    const storageItem =
      localStorage.getItem(Payment.getTransactionStorageKey(transactionStorageUuid)) ??
      this._uncheckedTransactionsCache.get(transactionStorageUuid);

    if (!storageItem) {
      this._logger.warning(
        `Checking payment status with transaction ID storage code, but without associated session.`,
        LOG_SOURCE,
      );
      return null;
    }

    const { transactionId, paymentProviderName } = JSON.parse(storageItem);

    const status = await this.request({
      method: 'GET',
      route: `${this._paymentManagementV2ApiUrl}/${transactionId}`,
      dataSource: PAYMENT_MANAGEMENT_V2,
      isImportant: true,
    }).then(response => response.status);

    if (PAYMENT_STATUS_MAP[status] === PAYMENT_STATUS_TYPES.UNCHECKED) {
      this._uncheckedTransactionsCache.set(transactionStorageUuid, storageItem);
    }

    localStorage.removeItem(Payment.getTransactionStorageKey(transactionStorageUuid));

    return { status, statusType: PAYMENT_STATUS_MAP[status], paymentProviderName };
  }
}
